import { useAuth } from "@/context/AuthContext";
import { useChildScope } from "@/context/ChildScopeContext";
import { Lock } from "lucide-react";

// v75 — Strict child-level isolation safeguard.
//
// Co-parents now only see children they've been explicitly invited to.
// When a co-parent has zero visible children (either because they were
// migrated off the legacy household model, or because the primary
// parent revoked all of their per-child memberships), the app is
// otherwise empty by design — no expenses, no events, no journal.
//
// Without this banner the user would just see "blank" pages and
// reasonably assume the app is broken. We surface a calm, non-alarming
// note explaining the state and how to resolve it.
//
// Hidden for:
//   • the primary parent (creator of the household — implicit access)
//   • mediators / view-only roles (they have their own permission UX)
//   • the operator (admin) account
//   • anyone who currently has at least one child visible
//   • households that haven't loaded yet (avoids flash on first paint)
export function NoChildrenAccessBanner() {
  const { user, isMediator } = useAuth();
  const { children, loaded } = useChildScope();

  if (!user) return null;
  if (user.is_primary_admin) return null;
  if (isMediator) return null;
  if (user.is_primary_parent) return null;
  if (!loaded) return null;
  if ((children || []).length > 0) return null;

  return (
    <div
      className="border border-[#E5C8A6] bg-[#FAF5EE] rounded-md px-4 py-3 mb-6 flex items-start gap-3"
      data-testid="no-children-access-banner"
      role="status"
    >
      <Lock className="w-4 h-4 text-[#825A41] mt-0.5 flex-shrink-0" strokeWidth={1.8} />
      <div className="text-sm">
        <div className="font-medium text-[#2E3A3F]">
          No children have been assigned to you yet.
        </div>
        <p className="text-[#6F828A] mt-0.5">
          Please ask the primary parent to grant you access to a specific child.
          They can do this from <span className="font-medium text-[#2E3A3F]">Children → Invite co-parent</span>.
        </p>
      </div>
    </div>
  );
}
